import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { MasonryGrid } from '@photosi/rn-components/masonry-grid/MasonryGrid';
import { MasonryItem } from '@photosi/rn-components/masonry-grid/MasonryItem';
import { observer } from 'mobx-react-lite';
import { ScrollView, View, useWindowDimensions, InteractionManager, Pressable } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import { PhotoCard } from '@photosi/rn-components/photo-card/PhotoCard';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { DynamicModalProps } from '../../DynamicModal.types';
import { ThumbnailImageModelInstance } from 'src/store/_models/thumbnailImage';
import { useMultiSelection } from 'src/hooks/use-multi-selection/useMultiSelection';
import { isWeb } from 'src/_shared/utils/isWeb';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents, AnalyticsSelectionTypes } from 'src/_shared/analyticsEvents';
import { SourcesEventType } from '../ModalUploadMultisource.constants';
import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import Spinner from '@photosi/rn-components/spinner/Spinner';
import { PickerComputerUploadContent } from '../picker-computer/picker-computer-upload-content/PickerComputerUploadContent';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { style } from './PickerComputer.styles';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { alert } from 'src/theme/colors';

export const PickerComputer = observer(
  ({
    dynamicModalLayer: { isUploadingPhoto, uploadMultiSourceCallback, groupCode: groupCodeDynamic },
    selectedCard,
  }: DynamicModalProps) => {
    const {
      project,
      i18n,
      toastStore,
      photoDrawer,
      dynamicModal: { closeModal, isModalVisible, openModal },
      data: { uploadError, fileListError },
    } = useStore();
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(0);
    const dropZoneRef = useRef(null);
    const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
    const images = project.activeProject?.thumbnailImages;
    const sortedImages = useMemo(
      () => images?.slice().sort((a, b) => b.fileAddedTimestamp - a.fileAddedTimestamp),
      [images, images?.length],
    );
    const { allowMultiSelect } = useMultiSelection([isModalVisible]);
    const code = groupCodeDynamic
      ? groupCodeDynamic
      : project?.activeGroup
        ? project?.activeGroup?.code
        : project?.groupCode;

    useEffect(() => {
      // Manage the drag and drop event only on the web
      if (isWeb()) {
        const dropZone = dropZoneRef.current as any;

        dropZone?.addEventListener('dragover', (event: React.DragEvent) => {
          if (!isDragging) {
            setIsDragging(true);
          }
          event.preventDefault();
        });

        dropZone?.addEventListener('dragenter', (event: React.DragEvent) => {
          setIsDragging(true);
          event.preventDefault();
        });

        dropZone?.addEventListener('dragleave', (event: React.DragEvent) => {
          setIsDragging(false);
          event.preventDefault();
        });

        dropZone?.addEventListener('drop-target', (event: React.DragEvent) => {
          event.preventDefault();
        });

        dropZone?.addEventListener('drop', async (event: React.DragEvent) => {
          setIsDragging(false);
          event.preventDefault();

          const droppedFiles: File[] = [...event.dataTransfer.files];
          filesToPickedImages(
            droppedFiles,
            {
              i18n,
              errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
                if (areAllPhotosInvalid) {
                  closeModal();
                }
                toastStore.showErrorToast({
                  id: 'alert-uploading-photos-error-toast',
                  message: errorMessage,
                });
              },
            },
            newRoute,
          ).then(async files => {
            if (newRoute) {
              const autoselect = project?.activeProject?.thumbnailImages.length == 0;
              files.forEach(file => (file.source = SELECTED_MENU_ITEMS.Computer));
              await project?.activeProject?.addImages(files).then(() => {
                if (autoselect) {
                  project?.activeProject?.thumbnailImages.forEach(image => {
                    photoDrawer.addImageId(image.originalImageId);
                  });
                }
              });
            } else {
              uploadMultiSourceCallback({
                files: files,
                isDragged: true,
                sourceType: SourcesEventType.Computer,
              });
            }

            amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
              n_photo: files.length,
              source_type: SourcesEventType.Computer,
            });
          });
        });
      }
    }, []);

    const onPressCard = (image: ThumbnailImageModelInstance, index: number) => {
      if (allowMultiSelect) {
        const from = Math.min(lastSelectedIndex, index);
        const to = Math.max(lastSelectedIndex, index);
        sortedImages?.forEach((thImage, thIndex) => {
          if (thIndex >= from && thIndex <= to && !thImage?.isSelected) {
            thImage.select();
            amplitude.trackEvent(AnalyticsEvents.dynamicPicker.photoSelectClick, {
              type: AnalyticsSelectionTypes.select,
            });
          }
        });
      } else {
        if (image.isSelected) {
          image.deselect();
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.photoSelectClick, {
            type: AnalyticsSelectionTypes.deselect,
          });
        } else {
          setLastSelectedIndex(index);
          image.select();
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.photoSelectClick, {
            type: AnalyticsSelectionTypes.select,
          });
        }
      }
    };

    const [zoomData, setZoomData] = useState({ x: 0, y: 0, width: 0, height: 0, uri: undefined });

    const imageRefs = useRef<Record<string, React.RefObject<View>>>({});

    sortedImages?.forEach(image => {
      if (!imageRefs.current[image.originalImageId]) {
        imageRefs.current[image.originalImageId] = createRef<View>();
      }
    });

    const scaleSV = useSharedValue(1);
    const opacitySV = useSharedValue(0);
    const translateXSV = useSharedValue(0);
    const translateYSV = useSharedValue(0);

    const onLongPress = (image: ThumbnailImageModelInstance) => {
      const ref = imageRefs.current[image.originalImageId];
      const scaleFactor = 3.5;
      ref.current?.measureInWindow((x, y, width, height) => {
        dropZoneRef.current?.measureInWindow((parentX, parentY, parentWidth, parentHeight) => {
          const relativeX = x - parentX;
          const relativeY = y - parentY;
          const imageCenterX = relativeX + width / 2;
          const imageCenterY = relativeY + height / 2;
          const viewportCenterX = parentWidth / 2;
          const viewportCenterY = parentHeight / 2;
          const targetTranslateX = viewportCenterX - imageCenterX;
          const targetTranslateY = viewportCenterY - imageCenterY;
          opacitySV.value = withTiming(1, { duration: 200, easing: Easing.cubic });
          translateXSV.value = withSpring(targetTranslateX, { damping: 14 });
          translateYSV.value = withSpring(targetTranslateY, { damping: 14 });
          scaleSV.value = withSpring(scaleFactor, { damping: 10, stiffness: 120 });
          setZoomData({ x: relativeX, y: relativeY, width, height, uri: image.thumbnailUrl });
        });
      });
    };

    const closeZoom = () => {
      scaleSV.value = withSpring(1, { damping: 12 });
      translateXSV.value = withSpring(0, { damping: 12 });
      translateYSV.value = withSpring(0, { damping: 12 });
      opacitySV.value = withTiming(0, { duration: 150 }, () => {
        runOnJS(setZoomData)({ x: 0, y: 0, width: 0, height: 0, uri: undefined });
      });
    };

    const { width } = useWindowDimensions();

    const zoomedStyle = useAnimatedStyle(() => {
      if (!zoomData.uri) return {};
      return {
        position: 'absolute',
        left: zoomData.x,
        top: zoomData.y,
        opacity: opacitySV.value,
        transform: [{ translateX: translateXSV.value }, { translateY: translateYSV.value }, { scale: scaleSV.value }],
        width: zoomData.width,
        height: zoomData.height,
        outlineColor: 'rgba(0,0,0,0.5)',
        outlineStyle: 'solid',
        outlineWidth: width,
      };
    });

    const scrollRef = useRef(null);
    const [scrollY, setScrollY] = useState(0);
    const prevLength = useRef(sortedImages?.length || 0);

    useEffect(() => {
      if (!sortedImages || scrollY === 0) return;
      const currentLength = sortedImages.length;
      if (currentLength !== prevLength.current) {
        InteractionManager.runAfterInteractions().then(() =>
          scrollRef.current?.scrollTo({
            y: scrollY,
            animated: false,
          }),
        );
      }
      prevLength.current = currentLength;
    }, [sortedImages?.length]);

    const fileErrorCount = fileListError.length;

    const openModalError = () => {
      openModal(
        {
          dynamicModalView: DYNAMIC_MODAL_VIEWS.ModalUploadError,
        },
        true,
      );
    };

    return (
      <View style={style.container} ref={dropZoneRef}>
        {isUploadingPhoto && !images?.length ? (
          <View style={style.spinner}>
            <Spinner />
          </View>
        ) : !!images?.length ? (
          <>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ height: '100%' }}
              showsVerticalScrollIndicator={false}
              ref={scrollRef}
              onScroll={e => setScrollY(e.nativeEvent.contentOffset.y)}
            >
              {!isUploadingPhoto && uploadError && (
                <View style={style.errorWrapper}>
                  <View style={style.errorInnerWrapper}>
                    <View style={style.icon}>
                      <Icon name={ICON_NAMES.Alert} color={alert.alertRed} />
                    </View>
                    <TextLato style={style.errorTitle}>{i18n.t('upload:upload-error-title')}</TextLato>
                  </View>
                  <View style={style.errorInnerWrapper}>
                    <TextLato style={style.errorDescriptionCenter}>
                      {i18n.t('upload:upload-error-description-head')}
                      <TextLato style={{ fontStyle: 'italic' }}>{fileListError?.slice(0, 3)?.join(', ')}</TextLato>
                      {fileErrorCount > 3
                        ? i18n.t('upload:upload-error-description-tail-counter', {
                            counter_file: fileErrorCount - 3,
                          })
                        : i18n.t('upload:upload-error-description-tail')}
                    </TextLato>
                  </View>
                  <View style={style.errorInnerWrapper}>
                    <Pressable onPress={openModalError}>
                      <TextLato style={style.errorTextLink}>{i18n.t('upload:show-error-list')}</TextLato>
                    </Pressable>
                  </View>
                </View>
              )}
              <MasonryGrid itemHeight={160} gap={8} outerGap={0}>
                {sortedImages?.map((image, index) => {
                  return (
                    <MasonryItem width={image.width} height={image.height} key={`grid-item-${image.originalImageId}`}>
                      <PhotoCard
                        image={image}
                        focusId={modalTestID(TestIDs.SELECT_PHOTO, index.toString())}
                        focusGroup={FocusGroups.MODAL}
                        focusOrder={index + 11}
                        testID={modalTestID(TestIDs.SELECT_PHOTO, index.toString())}
                        onPress={() => onPressCard(image, index)}
                        onLongPress={onLongPress}
                        closeZoom={closeZoom}
                        ref={imageRefs.current[image.originalImageId]}
                      />
                    </MasonryItem>
                  );
                })}
              </MasonryGrid>
            </ScrollView>
            {zoomData.uri && <Animated.Image source={zoomData.uri} style={zoomedStyle} />}
          </>
        ) : (
          <View style={style.spinner}>
            <PickerComputerUploadContent
              isDragging={isDragging}
              uploadMultiSourceCallback={uploadMultiSourceCallback}
              groupCode={code}
              selectedCard={selectedCard}
            />
            {!isUploadingPhoto && uploadError && (
              <View style={style.errorWrapperBottom}>
                <View style={style.errorInnerWrapper}>
                  <View style={style.icon}>
                    <Icon name={ICON_NAMES.Alert} color={alert.alertRed} />
                  </View>
                  <TextLato style={style.errorTitle}>{i18n.t('upload:upload-error-title')}</TextLato>
                </View>
                <View style={style.errorInnerWrapper}>
                  <TextLato style={style.errorDescriptionCenter}>
                    {i18n.t('upload:upload-error-description-head')}
                    <TextLato style={{ fontStyle: 'italic' }}>{fileListError?.slice(0, 3)?.join(', ')}</TextLato>
                    {fileErrorCount > 3
                      ? i18n.t('upload:upload-error-description-tail-counter', {
                          counter_file: fileErrorCount - 3,
                        })
                      : i18n.t('upload:upload-error-description-tail')}
                  </TextLato>
                </View>
                <View style={style.errorInnerWrapper}>
                  <Pressable onPress={openModalError}>
                    <TextLato style={style.errorTextLink}>{i18n.t('upload:show-error-list')}</TextLato>
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    );
  },
);
